type ArticleMessageLike = {
  type?: string
  content?: unknown
}

type ArticlePartMetadata = {
  article_id?: unknown
  message_index?: unknown
  message_count?: unknown
}

export type ArrivalArticleGroup = {
  startIndex: number
  endIndex: number
}

const getArticlePartMetadata = (message: ArticleMessageLike): ArticlePartMetadata | null => {
  if (message.type !== 'article' || !message.content || typeof message.content !== 'object') return null

  return message.content as ArticlePartMetadata
}

const getPositiveInteger = (value: unknown) =>
  typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : null

const getNonNegativeInteger = (value: unknown) =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : null

export const getArrivalArticleGroup = (messages: readonly ArticleMessageLike[]): ArrivalArticleGroup => {
  const startIndex = messages.findIndex(message => message.type === 'article')
  if (startIndex === -1) return { startIndex: -1, endIndex: -1 }

  let contiguousEndIndex = startIndex
  while (messages[contiguousEndIndex + 1]?.type === 'article') {
    contiguousEndIndex++
  }

  const firstPart = getArticlePartMetadata(messages[startIndex])
  const articleId = typeof firstPart?.article_id === 'string' && firstPart.article_id ? firstPart.article_id : null
  const messageCount = getPositiveInteger(firstPart?.message_count)
  const firstMessageIndex = getNonNegativeInteger(firstPart?.message_index)

  // Legacy article payloads did not expose split metadata. In that case, the
  // first consecutive run is the best available boundary.
  if (!articleId || messageCount === null || firstMessageIndex === null || firstMessageIndex >= messageCount) {
    return { startIndex, endIndex: contiguousEndIndex }
  }

  const receivedMessageIndices = new Set<number>()
  let lastArticlePartIndex = -1

  for (let index = startIndex; index <= contiguousEndIndex; index++) {
    const part = getArticlePartMetadata(messages[index])
    const messageIndex = getNonNegativeInteger(part?.message_index)
    const belongsToArrivalArticle =
      part?.article_id === articleId && getPositiveInteger(part.message_count) === messageCount

    if (!belongsToArrivalArticle || messageIndex === null || messageIndex >= messageCount) continue

    receivedMessageIndices.add(messageIndex)
    lastArticlePartIndex = index
  }

  if (receivedMessageIndices.size === messageCount) {
    return { startIndex, endIndex: lastArticlePartIndex }
  }

  // The article batch is still incomplete, so do not place the welcome between
  // two parts. The complete declared set will establish the final boundary.
  return { startIndex, endIndex: -1 }
}
